"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.simplifyFeatureCollection = exports.distSquared = void 0;
const turf_1 = require("@turf/turf");
const lodash_1 = __importDefault(require("lodash"));
const unit_conversion_1 = require("../calculators/unit-conversion");
const geojson_converters_1 = require("../geojson-converters");
const canopy_cover_1 = require("../calculators/canopy-cover");
// If the simplifyring operation results in an output where the output polygon
// is smaller than the input polygon, we should output an error message
// telling people something has gone wrong (somewhere). Because of floating point precision and
// is not really caring if small amounts of lost, we have a buffer.
const SIMPLIFY_SHRINK_AREA_WARN_M2 = 1.0;
/**
 * Computes the squared distance between two points allowing for
 * asymmetric coordinate system scaling
 *
 * @param ax PointA X
 * @param ay PointA Y
 * @param bx PointB X
 * @param by PointB Y
 * @param scaleX Scale the X vector by this factor
 * @param scaleY Scale the Y vector by this factor
 * @returns The distance between A and B
 */
function distSquared(ax, ay, bx, by, scaleX, scaleY) {
    const dX = (ax - bx) * scaleX;
    const dY = (ay - by) * scaleY;
    return dX * dX + dY * dY;
}
exports.distSquared = distSquared;
/**
 * Alters an array of positions: reducing the polycount while complying with the ETS rules
 *
 * Note, this method assumes polygons are wound clockwise, and holes anticlockwise. turf.rewind can ensure this.
 * Returns the area added to the polygon
 *
 * @param coords An array of positions to operate on. This array is mutated in place
 * @param maxEdgeLengthMeters Will not form edge lengths greater than this distance
 */
function simplifyRing(coords, maxEdgeLengthMeters) {
    // For the high level, see: https://carboncrop.atlassian.net/wiki/spaces/CAR/pages/117473413/ETS+Polygons+-+A+mathematically+rigorous+approach?focusedCommentId=122519564#5.-Simplify-polygon for the high level
    // This algorithm modifies the input polygon, and modifies the array during each step. This must be done, as removing a vertex can change if the vertices around it can be removed or not too.
    const [d2mx, d2my] = (0, unit_conversion_1.degreesToMetresFactor)(coords[0]);
    coords.pop(); // remove last element, as it is the same as the first, we'll add it in later
    const thresholdSquared = maxEdgeLengthMeters * maxEdgeLengthMeters;
    let i = 0; // the index of the point that is a candidate to remove. This will march around the ring
    let pointsLeftToCheck = coords.length;
    while (pointsLeftToCheck > 0 && coords.length > 3) {
        pointsLeftToCheck -= 1;
        // Find the indices either side of i, remembering that this is a ring!
        const iLeft = (i - 1 + coords.length) % coords.length;
        const iRight = (i + 1) % coords.length;
        // destructure out the x,y coords for each of the 3 points
        const [pLx, pLy] = coords[iLeft];
        const [px, py] = coords[i];
        const [pRx, pRy] = coords[iRight];
        // The 2 dimensional cross product can tell us if the angle is positive or negative, and hence concave or convex. This only works because we called turf.rewind to ensure the winding order is correct
        const crossProduct = (pLx - px) * (py - pRy) - (pLy - py) * (px - pRx);
        const isConcave = crossProduct <= 0;
        // Now for the magic! If it's concave, and the distance between iLeft and iRight is < 15m, then it means we can safely delete the middle point!
        if (isConcave && distSquared(pLx, pLy, pRx, pRy, d2mx, d2my) < thresholdSquared) {
            coords.splice(i, 1); // delete point i
            i -= 1; // move backwards after deleting a polygon. Why? This means we will sweep back and forth over the same area if there are multiple vertices to delete, increasing CPU cache hits.
            pointsLeftToCheck = coords.length;
        }
        else {
            // nothing to delete, onto the next
            i += 1;
        }
        i = (i + coords.length) % coords.length; // It's a ring! make sure that we wrap around
    }
    coords.push(coords[0]); // re-add last element, same as first element. It's a geojson thing.
}
/**
 * Simplifies a set of features. Note that this mutates the input as part of processing
 *
 * @param features The features to simplify
 * @param maxEdgeLengthMeters Will not form edge lengths greater than this distance
 * @returns A new bunch of features with reduced polycount
 */
function simplifyIteration(features, maxEdgeLengthMeters) {
    features.features.forEach((feature) => {
        (0, turf_1.rewind)(feature.geometry, { mutate: true });
        const oldArea = (0, turf_1.area)(feature);
        feature.geometry.coordinates.forEach((pointArray) => {
            simplifyRing(pointArray, maxEdgeLengthMeters);
        });
        const newArea = (0, turf_1.area)(feature);
        const areaChange = newArea - oldArea;
        if (areaChange < -SIMPLIFY_SHRINK_AREA_WARN_M2) {
            // eslint-disable-next-line no-console
            console.error(`SimplifyRing did shrink area by ${oldArea - newArea}sqm`);
        }
        // Safe because there is an external clone to prevent propagation
        // eslint-disable-next-line no-param-reassign
        feature.properties.canopyCoverProportion = (0, canopy_cover_1.calcNewCanopyCoverPercent)(oldArea, feature.properties.canopyCoverProportion, areaChange, 0.0);
    });
    const multiFeatureCollection = (0, turf_1.buffer)(features, 0);
    const singleFeatures = (0, geojson_converters_1.flattenMultiFeatureCollection)(multiFeatureCollection);
    return singleFeatures;
}
/**
 * Reduces the polycount of lots of features while obeying the ETS mapping rules.
 *
 * @param inputFeatureCollection Features to operate on
 * @param maxEdgeLengthMeters The simplification will never remove vertices to make an edge longer than this distance
 * @returns A new set of features with reduced polycount
 */
function simplifyFeatureCollection(inputFeatureCollection, maxEdgeLengthMeters) {
    let features = lodash_1.default.cloneDeep(inputFeatureCollection);
    features = simplifyIteration(features, maxEdgeLengthMeters);
    features = simplifyIteration(features, maxEdgeLengthMeters);
    features = simplifyIteration(features, maxEdgeLengthMeters);
    return features;
}
exports.simplifyFeatureCollection = simplifyFeatureCollection;
